/**
 * CreateLinkModal Component
 *
 * Modal for creating invitation links for end users
 */

import React, { useState, useEffect } from 'react';

import { Box, TextField, Switch, FormControlLabel } from '@mui/material';

import * as S from './styles';
import Modal from 'components/Modal';
import Text from 'components/Text';
import { useToast } from 'hooks/useToast';
import { LeadRequest, InvitationLink } from 'types/leadRequestsTypes';

interface InviteeFormData {
  invitee_first_name: string;
  invitee_last_name: string;
  invitee_cell_phone: string;
  invitee_email: string;
  notify_user: boolean;
}

interface CreateLinkModalProps {
  request: LeadRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onLinkCreated: (formData: InviteeFormData) => void;
  createdLink: InvitationLink | null;
  loading: boolean;
}

const CreateLinkModal: React.FC<CreateLinkModalProps> = ({
  request,
  isOpen,
  onClose,
  onLinkCreated,
  createdLink,
  loading,
}) => {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState<InviteeFormData>({
    invitee_first_name: '',
    invitee_last_name: '',
    invitee_cell_phone: '',
    invitee_email: '',
    notify_user: true,
  });
  const { toastSuccess, toastError } = useToast();

  useEffect(() => {
    if (createdLink && isOpen) {
      // Reset copied state when new link is created
      setCopied(false);
    }
    // Reset form when modal opens
    if (isOpen && !createdLink) {
      setFormData({
        invitee_first_name: '',
        invitee_last_name: '',
        invitee_cell_phone: '',
        invitee_email: '',
        notify_user: true,
      });
    }
  }, [createdLink, isOpen]);

  if (!request) return null;

  const fullLink = createdLink
    ? `${window.location.origin}/register?invite_id=${createdLink.link}`
    : '';

  const handleCopyLink = async () => {
    if (!fullLink) return;

    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      toastSuccess('Link copiado para a área de transferência!');

      // Reset copied state after 3 seconds
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Erro ao copiar link:', err);
    }
  };

  const handleClose = () => {
    setCopied(false);
    setFormData({
      invitee_first_name: '',
      invitee_last_name: '',
      invitee_cell_phone: '',
      invitee_email: '',
      notify_user: true,
    });
    onClose();
  };

  const handleInputChange =
    (field: keyof InviteeFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = field === 'notify_user' ? e.target.checked : e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
    };

  const handleCreateLink = () => {
    // Validate required fields
    if (!formData.invitee_first_name.trim()) {
      toastError('Por favor, preencha o nome do convidado');
      return;
    }
    if (!formData.invitee_last_name.trim()) {
      toastError('Por favor, preencha o sobrenome do convidado');
      return;
    }
    if (!formData.invitee_email.trim()) {
      toastError('Por favor, preencha o email do convidado');
      return;
    }
    if (!formData.invitee_cell_phone.trim()) {
      toastError('Por favor, preencha o telefone do convidado');
      return;
    }
    onLinkCreated(formData);
  };

  return (
    <Modal
      closeModal={handleClose}
      title="Criar link de convite"
      onSecondaryClick={handleClose}
      secondaryTextButton="Fechar"
      onPrimaryClick={createdLink ? handleCopyLink : handleCreateLink}
      primaryTextButton={createdLink ? (copied ? 'Copiado!' : 'Copiar link') : 'Criar link'}
      primaryButtonDisabled={loading}
    >
      <S.ModalContent>
        <Box mb={3}>
          <Text size="medium" color="textSecondary">
            Crie um link de convite para um usuário final responder a esta solicitação de
            consultoria.
          </Text>
        </Box>

        <S.ModalSection>
          <S.ModalSectionTitle>Informações da solicitação</S.ModalSectionTitle>
          <S.FormDataDisplay>
            <S.FormDataRow>
              <S.FormDataLabel>ID da solicitação</S.FormDataLabel>
              <S.FormDataValue>{request.id}</S.FormDataValue>
            </S.FormDataRow>
            <S.FormDataRow>
              <S.FormDataLabel>ID do solicitante:</S.FormDataLabel>
              <S.FormDataValue>{request.requester_user_id}</S.FormDataValue>
            </S.FormDataRow>
          </S.FormDataDisplay>
        </S.ModalSection>

        {!createdLink && (
          <S.ModalSection>
            <S.ModalSectionTitle>Dados do convidado</S.ModalSectionTitle>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Nome"
                value={formData.invitee_first_name}
                onChange={handleInputChange('invitee_first_name')}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Sobrenome"
                value={formData.invitee_last_name}
                onChange={handleInputChange('invitee_last_name')}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Email"
                type="email"
                value={formData.invitee_email}
                onChange={handleInputChange('invitee_email')}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Telefone"
                value={formData.invitee_cell_phone}
                onChange={handleInputChange('invitee_cell_phone')}
                required
                fullWidth
                variant="outlined"
                placeholder="+5599999999999"
                helperText="Formato: +55XXXXXXXXXXX"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notify_user}
                    onChange={handleInputChange('notify_user')}
                    color="primary"
                  />
                }
                label="Notificar usuário por email"
              />
            </Box>
          </S.ModalSection>
        )}

        {createdLink && (
          <S.ModalSection>
            <S.ModalSectionTitle>Link criado</S.ModalSectionTitle>
            <S.LinkDisplayBox>
              <S.LinkText>{fullLink}</S.LinkText>
              <S.CopyButton onClick={handleCopyLink}>
                {copied ? '✓ Copiado!' : '📋 Copiar para a área de transferência'}
              </S.CopyButton>
            </S.LinkDisplayBox>

            <Box mt={2}>
              <Text size="small" color="textSecondary">
                Compartilhe este link com o usuário final via email ou plataforma de mensagens
              </Text>
            </Box>
          </S.ModalSection>
        )}
      </S.ModalContent>
    </Modal>
  );
};

export default CreateLinkModal;
